import { css } from "@linaria/core";
import { useRef } from "react";

import { pm2Boundaries, pm2Header, pm2Transitions } from "@pm2/design-tokens";
import { breakpoints, units } from "@ui/css";
import { colors } from "@ui/kit";

type Props = {
  children: React.ReactNode;
};

export function Boundary({ children }: Props): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);

  pm2Boundaries.useContentEngineBoundaryProps(ref);

  return (
    <div ref={ref} className={wrapperCls} id="main">
      {children}
    </div>
  );
}

const wrapperCls = css`
  transition: ${pm2Transitions.headerCollapse({ transform: ["padding-top"] })};

  /**
   * always fill the available part of the viewport height-wise, so that the
   * new page will always be easily able to stretch to the bottom of the
   * viewport.
   */
  box-sizing: border-box;
  min-height: calc(
    ${units.vh(100)} - ${pm2Header.height.computed()} +
      ${pm2Header.breadcrumbs.computed()}
  );

  /**
   * configure boundary to stretch to the desired maximum column size within
   * the layout
   */
  max-width: ${units.px(1110)};
  margin: 0 auto;

  /**
   * using flex-box inside to allow footers to easily push to the bottom of
   * viewport on small pages
   */
  display: flex;
  flex-direction: column;

  /**
   * handle all positioning internally so that it's relative to the boundary at
   * all times
   */
  position: relative;

  /**
   * basic styling of nested content
   */
  color: ${colors.text.primary()};

  /**
   * add spacer to fill out if breadcrumbs are available on the current page.
   */
  padding-top: ${pm2Header.breadcrumbs.computed()};

  @media print {
    padding-top: 0 !important;
  }

  /**
   * apply nested height configuration, so that we always know exactly what the
   * available content height is, taking into account any reserved height for
   * the header.
   */
  @media ${breakpoints.tablet.andUp().toQuery()} {
    ${pm2Boundaries.container.height.__override(units.vh(100)).toString()};
  }

  @media ${breakpoints.mobile.toQuery()} {
    ${pm2Boundaries.container.height
      .__override(
        `calc(${units.vh(
          100,
        )} - ${pm2Header.height.collapsed()} - ${pm2Header.breadcrumbs.computed()})`,
      )
      .toString()};
  }
`;
